<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaCheckMenu, {
  type GaiaCheckMenuOption,
} from '@/components/design/composite/GaiaCheckMenu.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import { IconEdit, IconShare } from '@/components/design/icons';
import MassShareContractsModal from '@/components/contractManagement/MassShareContractsModal.vue';
import {
  VaultShareService,
  type VaultShareEntry,
  type VaultShareTarget,
} from '@/services/vaultShareService';
import type { AccessPermission } from '@/api';
import { ACCESS_PERMISSION_FLAGS, formatAccessPermissions } from '@/utils/accessPermission';
import { initialsOf } from '@/utils/initials';
import type { VaultUidGroups } from '@/utils/vaultSelection';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * Who one vault item — a folder, a plain file or a contract — is shared with,
 * and the way to share it further. Only direct shares are listed: the owner,
 * org admins and grants inherited from a folder above never appear here.
 * Changing or removing a share needs ADMIN on the item; the backend refuses
 * otherwise.
 */
const props = defineProps<{
  show: boolean;
  /** Null while nothing is picked; the dialog stays closed then. */
  target: VaultShareTarget | null;
  name: string;
}>();

const emit = defineEmits<{ 'update:show': [value: boolean] }>();

const message = useGaiaToast();

const NOUN: Record<VaultShareTarget['kind'], string> = {
  file: 'file',
  directory: 'folder',
  contract: 'contract',
};

interface ShareRow {
  key: string;
  kind: 'user' | 'role';
  uid: string;
  name: string;
  meta: string;
  permissions: AccessPermission;
}

function toRow(entry: VaultShareEntry): ShareRow | null {
  if (entry.user) {
    return {
      key: `user-${entry.user.uid}`,
      kind: 'user',
      uid: entry.user.uid,
      name: entry.user.full_name || entry.user.email,
      meta: entry.user.email,
      permissions: entry.permissions,
    };
  }
  if (entry.role) {
    return {
      key: `role-${entry.role.uid}`,
      kind: 'role',
      uid: entry.role.uid,
      name: entry.role.name,
      meta: 'Role',
      permissions: entry.permissions,
    };
  }
  return null;
}

const rows = ref<ShareRow[]>([]);
const loading = ref(false);
const removingKey = ref<string | null>(null);
const showShareModal = ref(false);

async function load(): Promise<void> {
  const target = props.target;
  if (!target) return;
  loading.value = true;
  try {
    const entries = await VaultShareService.getShares(target);
    if (props.target !== target) return;
    rows.value = entries.flatMap(entry => toRow(entry) ?? []);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load shares');
    rows.value = [];
  } finally {
    loading.value = false;
  }
}

async function remove(row: ShareRow): Promise<void> {
  const target = props.target;
  if (!target || removingKey.value) return;
  removingKey.value = row.key;
  try {
    await (row.kind === 'user'
      ? VaultShareService.removeUserShare(target, row.uid)
      : VaultShareService.removeRoleShare(target, row.uid));
    message.success(`${row.name} no longer has shared access`);
    await load();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to remove share');
  } finally {
    removingKey.value = null;
  }
}

// ── Change one share's access ───────────────────────────────────────────────

const editingKey = ref<string | null>(null);
const editBitmask = ref(0);
const savingEdit = ref(false);

function openEdit(row: ShareRow): void {
  editingKey.value = row.key;
  editBitmask.value = row.permissions;
}

const editOptions = computed<GaiaCheckMenuOption[]>(() =>
  ACCESS_PERMISSION_FLAGS.map(flag => ({
    key: String(flag.value),
    label: flag.label,
    checked: (editBitmask.value & flag.value) !== 0,
  }))
);

async function saveEdit(row: ShareRow): Promise<void> {
  const target = props.target;
  if (!target || savingEdit.value) return;
  if (editBitmask.value === 0) {
    message.warning('Select at least one permission');
    return;
  }
  savingEdit.value = true;
  try {
    const permissions = editBitmask.value;
    await (row.kind === 'user'
      ? VaultShareService.setUserShare(target, row.uid, permissions)
      : VaultShareService.setRoleShare(target, row.uid, permissions));
    message.success('Permissions updated');
    editingKey.value = null;
    await load();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to update permissions');
  } finally {
    savingEdit.value = false;
  }
}

const shareGroups = computed<VaultUidGroups>(() => {
  const target = props.target;
  return {
    fileUids: target?.kind === 'file' ? [target.uid] : [],
    contractUids: target?.kind === 'contract' ? [target.uid] : [],
    directoryUids: target?.kind === 'directory' ? [target.uid] : [],
  };
});

const noun = computed(() => (props.target ? NOUN[props.target.kind] : 'item'));

watch(
  () => [props.show, props.target] as const,
  ([show]) => {
    if (!show) return;
    rows.value = [];
    editingKey.value = null;
    void load();
  },
  { immediate: true }
);
</script>

<template>
  <gaia-modal
    :show="props.show && props.target !== null"
    width="560px"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <template #header>
      <gaia-text size="subtitle" weight="semibold" tone="ink">Share “{{ props.name }}”</gaia-text>
    </template>

    <n-flex vertical :size="12">
      <n-flex align="center" :size="8" :wrap="false">
        <gaia-text size="control" tone="subtle">
          Users and roles this {{ noun }} is shared with directly. Its owner, org admins and anyone
          a folder above it is shared with can open it too.
          <template v-if="props.target?.kind === 'directory'">
            Everything inside the folder inherits these shares.
          </template>
        </gaia-text>
        <gaia-button
          type="primary"
          size="medium"
          :style="{ marginLeft: 'auto', flexShrink: 0 }"
          @click="showShareModal = true"
        >
          <template #icon><n-icon :component="IconShare" /></template>
          Share
        </gaia-button>
      </n-flex>

      <gaia-table-skeleton
        v-if="loading && rows.length === 0"
        :row-style="{ gridTemplateColumns: 'minmax(0, 1fr) 120px' }"
        :cells="['46%', '96px']"
        :rows="3"
        :row-height="52"
        :action="false"
      />
      <gaia-empty-state v-else-if="rows.length === 0" title="Not shared with anyone yet" dashed>
        <template #icon><n-icon :component="IconShare" :size="18" /></template>
        Share this {{ noun }} to give users or roles access to it.
      </gaia-empty-state>
      <n-flex v-else vertical :size="0">
        <n-flex
          v-for="row in rows"
          :key="row.key"
          align="center"
          :size="10"
          :wrap="false"
          :style="{ padding: '10px 0' }"
        >
          <gaia-avatar :size="26" :type="row.kind === 'role' ? 'mark' : undefined">
            {{ initialsOf(row.name) }}
          </gaia-avatar>
          <n-flex vertical :size="0" :style="{ minWidth: 0, flex: 1 }">
            <gaia-text truncate size="control" weight="semibold" tone="ink">
              {{ row.name }}
            </gaia-text>
            <gaia-text truncate size="meta" tone="subtle">{{ row.meta }}</gaia-text>
          </n-flex>
          <gaia-tag variant="muted">{{ formatAccessPermissions(row.permissions) }}</gaia-tag>
          <gaia-popover
            :show="editingKey === row.key"
            trigger="click"
            placement="bottom-end"
            @update:show="(value: boolean) => !value && (editingKey = null)"
          >
            <template #trigger>
              <gaia-icon-button
                :dimension="24"
                quaternary
                title="Edit permissions"
                @click.stop="openEdit(row)"
              >
                <n-icon :component="IconEdit" :size="12" />
              </gaia-icon-button>
            </template>
            <gaia-check-menu
              title="Permissions"
              :options="editOptions"
              :loading="savingEdit"
              @toggle="(key: string) => (editBitmask ^= Number(key))"
              @cancel="editingKey = null"
              @confirm="saveEdit(row)"
            />
          </gaia-popover>
          <gaia-delete-button
            intent="remove"
            size="xs"
            subject="access"
            :loading="removingKey === row.key"
            @click.stop="remove(row)"
          />
        </n-flex>
      </n-flex>
    </n-flex>

    <mass-share-contracts-modal
      v-model:show="showShareModal"
      kind="vault"
      :vault-groups="shareGroups"
      :contract-names="[props.name]"
      @shared="load"
    />
  </gaia-modal>
</template>
